import React, { useEffect, useState } from "react";
import SuperAdminSidebar from "../components/SuperAdminSidebar";
import api from "../lib/api";
import { toast } from "sonner";
import { FileText, CheckCircle2, Clock, ChevronRight, Calendar, Users, X } from "lucide-react";

export default function SuperAdminBilling() {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/superadmin/billing", { params: filter ? { status: filter } : {} });
            setList(r.data);
        } catch {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const openDetail = async (invoice_id) => {
        setDetailLoading(true);
        try {
            const r = await api.get(`/superadmin/billing/${invoice_id}`);
            setDetail(r.data);
        } catch {
            toast.error("Errore dettaglio");
        } finally {
            setDetailLoading(false);
        }
    };

    const totals = list.reduce((acc, inv) => {
        acc.total_cents += inv.amount_cents || 0;
        if (inv.status === "paid") acc.paid_cents += inv.amount_cents || 0;
        else acc.pending_cents += inv.amount_cents || 0;
        return acc;
    }, { total_cents: 0, paid_cents: 0, pending_cents: 0 });

    return (
        <div className="flex min-h-screen bg-ink" data-testid="superadmin-billing-page">
            <SuperAdminSidebar />
            <main className="flex-1 p-10 space-y-8">
                <div>
                    <div className="text-gold text-xs tracking-[0.4em] uppercase">Renova Central</div>
                    <h1 className="font-serif text-5xl text-white mt-2">Fatturazione ristoranti</h1>
                    <p className="text-gray-400 text-sm mt-2">Elenco fatture commissioni. Clicca una riga per vedere le prenotazioni conguagliate.</p>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                    <Stat label="Totale fatture" value={`€ ${(totals.total_cents / 100).toFixed(2)}`} />
                    <Stat label="Incassate" value={`€ ${(totals.paid_cents / 100).toFixed(2)}`} positive />
                    <Stat label="In sospeso" value={`€ ${(totals.pending_cents / 100).toFixed(2)}`} pending />
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400">Filtro</span>
                    {["", "pending", "paid", "failed"].map((v) => (
                        <button key={v || "all"} onClick={() => setFilter(v)}
                            className={`text-xs px-3 py-1.5 rounded-sm border uppercase tracking-widest ${filter === v ? "border-gold text-gold bg-gold/10" : "border-white/10 text-gray-400 hover:border-white/30"}`}
                            data-testid={`filter-${v || "all"}`}>
                            {v === "" ? "Tutte" : v}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="text-gray-500">Caricamento...</div>
                ) : list.length === 0 ? (
                    <div className="bg-ink-surface border border-white/10 rounded-sm p-12 text-center text-gray-500" data-testid="empty-billing">
                        <FileText size={32} className="mx-auto mb-3" strokeWidth={1} />
                        Nessuna fattura in questo stato.
                    </div>
                ) : (
                    <div className="bg-ink-surface border border-white/10 rounded-sm overflow-hidden" data-testid="billing-list">
                        <table className="w-full text-sm">
                            <thead className="bg-ink/50 text-[10px] uppercase tracking-widest text-gray-400">
                                <tr>
                                    <th className="text-left px-5 py-3">Data</th>
                                    <th className="text-left px-5 py-3">Ristorante</th>
                                    <th className="text-left px-5 py-3">Prenotazioni</th>
                                    <th className="text-left px-5 py-3">Stato</th>
                                    <th className="text-right px-5 py-3">Importo</th>
                                    <th className="w-8" />
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((inv) => (
                                    <tr key={inv.invoice_id} className="border-t border-white/5 hover:bg-white/2 cursor-pointer"
                                        onClick={() => openDetail(inv.invoice_id)}
                                        data-testid={`invoice-row-${inv.invoice_id}`}>
                                        <td className="px-5 py-3 text-white">{(inv.paid_at || inv.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                        <td className="px-5 py-3 text-gold">{inv.restaurant_name}</td>
                                        <td className="px-5 py-3 text-gray-400">{inv.reservation_ids?.length || 0}</td>
                                        <td className="px-5 py-3">
                                            {inv.status === "paid" ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 size={13} /> Pagata</span>
                                            ) : inv.status === "pending" ? (
                                                <span className="inline-flex items-center gap-1 text-amber-400 text-xs"><Clock size={13} /> In attesa</span>
                                            ) : (
                                                <span className="text-xs text-red-400 uppercase">{inv.status}</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right text-gold font-semibold">€ {(inv.amount_cents / 100).toFixed(2)}</td>
                                        <td className="px-3 py-3 text-gray-500"><ChevronRight size={14} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {/* Detail drawer */}
            {detail && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6 overflow-y-auto" onClick={() => setDetail(null)}>
                    <div onClick={(e) => e.stopPropagation()}
                        className="bg-ink-surface border border-gold/30 rounded-sm max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto" data-testid="invoice-detail">
                        <div className="sticky top-0 bg-ink-surface/95 backdrop-blur border-b border-white/10 p-6 flex items-start justify-between gap-4">
                            <div>
                                <div className="text-gold text-[10px] tracking-[0.3em] uppercase">Fattura</div>
                                <h2 className="font-serif text-3xl text-white">{detail.restaurant_name}</h2>
                                <div className="text-xs text-gray-500 mt-1">ID: <code className="text-gold">{detail.invoice_id}</code></div>
                            </div>
                            <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-white" data-testid="close-detail-btn">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="grid grid-cols-3 gap-4">
                                <InfoBlock label="Importo" value={`€ ${(detail.amount_cents / 100).toFixed(2)}`} gold />
                                <InfoBlock label="Stato" value={detail.status.toUpperCase()} />
                                <InfoBlock label="Prenotazioni" value={detail.reservations?.length || 0} />
                            </div>
                            <div>
                                <h3 className="font-serif text-xl text-white mb-3">Prenotazioni conguagliate</h3>
                                {!detail.reservations?.length ? (
                                    <div className="text-gray-500 text-sm">Nessun dettaglio disponibile.</div>
                                ) : (
                                    <div className="bg-ink border border-white/10 rounded-sm overflow-hidden" data-testid="invoice-reservations">
                                        <table className="w-full text-sm">
                                            <thead className="bg-ink/50 text-[10px] uppercase tracking-widest text-gray-400">
                                                <tr>
                                                    <th className="text-left px-4 py-2">Data</th>
                                                    <th className="text-left px-4 py-2">Cliente</th>
                                                    <th className="text-left px-4 py-2">Coperti</th>
                                                    <th className="text-right px-4 py-2">Commissione</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.reservations.map((r) => (
                                                    <tr key={r.reservation_id} className="border-t border-white/5">
                                                        <td className="px-4 py-2 text-white">
                                                            <Calendar size={11} className="inline text-gold mr-1" />
                                                            {r.date} · {r.slot_time}
                                                        </td>
                                                        <td className="px-4 py-2 text-gray-300">{r.customer_name}</td>
                                                        <td className="px-4 py-2 text-gray-400">
                                                            <Users size={11} className="inline text-gold mr-1" />{r.party_size}
                                                        </td>
                                                        <td className="px-4 py-2 text-right text-gold">€ {(r.commission_cents / 100).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {detailLoading && (
                <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center">
                    <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, positive, pending }) {
    const cls = positive ? "text-emerald-400" : pending ? "text-amber-400" : "text-white";
    return (
        <div className="bg-ink-surface border border-white/10 rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400">{label}</div>
            <div className={`font-serif text-3xl mt-2 ${cls}`}>{value}</div>
        </div>
    );
}

function InfoBlock({ label, value, gold }) {
    return (
        <div className="bg-ink border border-white/10 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400">{label}</div>
            <div className={`font-serif text-2xl mt-1 ${gold ? "text-gold" : "text-white"}`}>{value}</div>
        </div>
    );
}
